import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { Mail, Phone, MapPin } from 'lucide-react';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        budget: '',
        service: '',
        message: ''
    });
    const [errors, setErrors] = useState({});

    const form = useRef();

    const schemaData = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "ContactPage",
                "@id": "https://pyrusmedia.in/contact#contact",
                "url": "https://pyrusmedia.in/contact",
                "name": "Contact PyrusMedia",
                "description": "Contact PyrusMedia for digital marketing, web development, mobile applications, AI automation, and IT solutions.",
                "isPartOf": {
                    "@id": "https://pyrusmedia.in/#website"
                },
                "about": {
                    "@type": "Organization",
                    "name": "PyrusMedia",
                    "url": "https://pyrusmedia.in/",
                    "telephone": "+917720830178",
                    "email": "info@pyrusmedia.in"
                }
            },
            {
                "@type": "LocalBusiness",
                "name": "PyrusMedia",
                "image": "https://pyrusmedia.in/logo.svg",
                "url": "https://pyrusmedia.in/contact",
                "telephone": "+917720830178",
                "email": "info@pyrusmedia.in",
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "D408 The Atrium, BG Shirke Rd, Near Magarpatta Mundhwa",
                    "addressLocality": "Pune",
                    "addressRegion": "Maharashtra",
                    "postalCode": "411036",
                    "addressCountry": "IN"
                },
                "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": 18.5186,
                    "longitude": 73.9324
                },
                "areaServed": ["Pune", "India"],
                "openingHours": "Mo-Fr 09:00-18:00"
            }
        ]
    };

    const validate = () => {
        let tempErrors = {};
        if (!formData.name) tempErrors.name = "Name is required";
        if (!formData.email) {
            tempErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            tempErrors.email = "Email is invalid";
        }
        if (!formData.message) tempErrors.message = "Message is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validate()) {
            
            // EMAILJS CONFIGURATION
            // Replace these with your actual keys from https://dashboard.emailjs.com/
            const SERVICE_ID = 'service_k58equi';
            const TEMPLATE_ID = 'template_zoxjs9f';
            const PUBLIC_KEY = 'OPcmD1RuZ7WBh3Bwj';

            if (SERVICE_ID === 'YOUR_SERVICE_ID') {
                alert('EmailJS is not configured yet! Please add your Service ID, Template ID, and Public Key in src/pages/Contact.jsx');
                console.warn('EmailJS keys are missing.');
                return;
            }

            emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY)
                .then((result) => {
                    console.log('Email sent successfully:', result.text);
                    alert('Thank you! Your message has been sent successfully.');
                    setFormData({ name: '', email: '', phone: '', company: '', budget: '', service: '', message: '' });
                    setErrors({});
                }, (error) => {
                    console.error('Email sending failed:', error.text);
                    alert('Oops! Something went wrong. Please try again later or contact us directly via phone/email.');
                });
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: null });
        }
    };

    return (
        <>
            <Helmet>
                <title>Contact Pyrus Media- Get a Free Digital Marketing Quote</title>
                <meta name="description" content="Get in touch with PyrusMedia in Magarpatta, Pune. Contact our experts for web application development, mobile app development, SEO services, and AI automation." />
                <meta name="keywords" content="PyrusMedia contact, digital marketing agency Pune contact, web development company Pune address, IT solutions Pune" />
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            </Helmet>

            <section className="pt-32 pb-20 bg-white text-center">
                <div className="container mx-auto px-4">
                    <h1 className="text-5xl font-bold text-gray-900 mb-6">Let's Talk About Your Project</h1>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                        Ready to grow? Fill out the form below and we'll get back to you within 24 hours.
                    </p>
                </div>
            </section>

            <Section className="bg-white">
                <div className="flex flex-col lg:flex-row gap-16">
                    {/* Contact Info */}
                    <div className="lg:w-1/3 space-y-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                            <p className="text-gray-500 mb-8">
                                Have a question or want to discuss a partnership? We're here to help.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                                    <Mail className="text-[#bebd19] w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="text-gray-900 font-bold mb-1">Email Us</h4>
                                    <a href="mailto:info@pyrusmedia.in" className="text-gray-500 hover:text-[#bebd19]">info@pyrusmedia.in</a>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                                    <Phone className="text-[#bebd19] w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="text-gray-900 font-bold mb-1">Call / WhatsApp</h4>
                                    <a href="tel:+917720830178" className="text-gray-500 hover:text-[#bebd19]">+91 77208 30178</a>
                                </div>
                            </div>
                          
                             <div className="flex items-start gap-4"> 
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                                    <MapPin className="text-red-500 w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="text-gray-900 font-bold mb-1">Visit Us</h4>
                                    <p className="text-gray-500">D408 The Atrium, BG Shirke Rd, Near Magarpatta Mundhwa, Pune, Maharashtra 411036</p>
                                </div>
                            </div> 
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:w-2/3 bg-gray-100 p-8 md:p-10 rounded-2xl border border-gray-200">
                        <form ref={form} onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-gray-500 text-sm mb-2">Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name} onChange={handleChange}
                                    className={`w-full bg-white border rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19] ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                            </div>
                            <div>
                                <label className="block text-gray-500 text-sm mb-2">Email Address *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email} onChange={handleChange}
                                    className={`w-full bg-white border rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19] ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                            </div>
                            <div>
                                <label className="block text-gray-500 text-sm mb-2">Phone / WhatsApp</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone} onChange={handleChange}
                                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19]"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-500 text-sm mb-2">Company Name</label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company} onChange={handleChange}
                                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19]"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-500 text-sm mb-2">Budget Range</label>
                                <select
                                    name="budget"
                                    value={formData.budget} onChange={handleChange}
                                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19]"
                                >
                                    <option value="">Select Budget</option>
                                    <option value="under-1k">&lt; $1,000</option>
                                    <option value="1k-5k">$1,000 - $5,000</option>
                                    <option value="5k-10k">$5,000 - $10,000</option>
                                    <option value="10k+">$10,000+</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-500 text-sm mb-2">Service Interested In</label>
                                <select
                                    name="service"
                                    value={formData.service} onChange={handleChange}
                                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19]"
                                >
                                    <option value="">Select Service</option>
                                    <option value="marketing">Digital Marketing</option>
                                    <option value="design">UX/UI Design</option>
                                    <option value="web">Web Development</option>
                                    <option value="mobile">Mobile Development</option>
                                    <option value="ai">AI Automation</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-gray-500 text-sm mb-2">Your Message *</label>
                                <textarea
                                    name="message"
                                    value={formData.message} onChange={handleChange} rows="5"
                                    className={`w-full bg-white border rounded-lg p-3 text-gray-900 focus:outline-none focus:border-[#bebd19] ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                                ></textarea>
                                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                            </div>
                            <div className="md:col-span-2">
                                <Button type="submit" className="w-full justify-center py-4 text-lg">Send Message</Button>
                            </div>
                        </form>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default Contact;
